import { useState } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, MagnifyingGlass, ArrowRight, Wrench, ClipboardText, Funnel, Star } from '@phosphor-icons/react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatRelativeTime } from '@/lib/utils';
import useAppStore from '@/stores/appStore';

const CLOSED_STATUSES = ['closed', 'completed', 'rejected'];

export const Tickets = () => {
  const { data: tickets, isLoading } = useTickets();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'past' ? 'past' : 'active';
  const [searchQuery, setSearchQuery] = useState('');
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';

  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const allTickets = tickets?.data || [];
  const activeTickets = allTickets.filter(t => !CLOSED_STATUSES.includes(t.status));
  const closedTickets = allTickets.filter(t => CLOSED_STATUSES.includes(t.status));

  const currentList = activeTab === 'active' ? activeTickets : closedTickets;

  // Search
  const filteredTickets = currentList.filter(ticket =>
    ticket.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.issue_category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort newest first
  const sortedTickets = [...filteredTickets].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <DashboardLayout role="customer">
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">My Tickets</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track the status of your service requests</p>
          </div>
          <Link to="/customer/tickets/new">
            <Button
              className="font-bold text-white shadow-sm px-5 h-10"
              style={{ backgroundColor: brandColor }}
              data-testid="raise-new-ticket-btn"
            >
              <Plus size={18} weight="bold" className="mr-2" />
              Raise Ticket
            </Button>
          </Link>
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1 shrink-0">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                activeTab === 'active'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-active"
            >
              Active ({activeTickets.length})
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                activeTab === 'past'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-past"
            >
              Past ({closedTickets.length})
            </button>
          </div>
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-white"
              data-testid="search-tickets-input"
            />
          </div>
        </div>

        {/* Ticket List */}
        {isLoading ? (
          <LoadingSpinner />
        ) : sortedTickets.length > 0 ? (
          <div className="space-y-3">
            {sortedTickets.map((ticket) => (
              <Link key={ticket.id} to={`/customer/tickets/${ticket.id}`}>
                <Card
                  className={`p-4 hover:shadow-md transition-all cursor-pointer group ${
                    CLOSED_STATUSES.includes(ticket.status)
                      ? 'border-slate-100 bg-slate-50/30'
                      : 'border-l-4'
                  }`}
                  style={!CLOSED_STATUSES.includes(ticket.status) ? { borderLeftColor: brandColor } : undefined}
                  data-testid={`ticket-card-${ticket.ticket_number}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-black text-sm text-slate-900">{ticket.ticket_number}</span>
                        <StatusBadge status={ticket.status} />
                      </div>
                      <p className="font-semibold text-sm text-slate-700">{ticket.product_name || 'Registered Product'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{ticket.issue_category}</p>
                      {ticket.tech_name && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                          <Wrench size={12} />
                          <span className="font-medium">{ticket.tech_name}</span>
                          {CLOSED_STATUSES.includes(ticket.status) && (
                            ticket.is_rated ? (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                                <Star size={10} weight="fill" /> Rated {ticket.rating_stars}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-white bg-amber-500 hover:bg-amber-600 px-1.5 py-0.5 rounded shadow-sm animate-pulse ml-2">
                                <Star size={10} weight="fill" /> Rate Technician
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="text-[11px] text-slate-400 font-medium">
                        {formatRelativeTime(ticket.created_at)}
                      </span>
                      <ArrowRight size={16} className="text-slate-300 group-hover:text-slate-600 transition-colors group-hover:translate-x-0.5 transform" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="p-12 sm:p-16 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
            <div className="p-4 bg-slate-100 rounded-2xl inline-flex mb-4">
              <ClipboardText size={40} className="text-slate-400" />
            </div>
            <h3 className="font-bold text-lg text-slate-700 mb-1">
              {searchQuery ? 'No matching tickets' : activeTab === 'active' ? 'No active tickets' : 'No past tickets'}
            </h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              {searchQuery
                ? 'Try adjusting your search query.'
                : activeTab === 'active'
                  ? "You don't have any ongoing service requests."
                  : 'Completed and closed tickets will appear here.'
              }
            </p>
            {!searchQuery && activeTab === 'active' && (
              <Link to="/customer/tickets/new">
                <Button className="font-bold px-6 h-11" style={{ backgroundColor: brandColor }}>
                  <Plus size={18} className="mr-2" />
                  Raise Your First Ticket
                </Button>
              </Link>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
